import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type Header } from '@tanstack/react-table';
import { debounce } from 'lodash';

import { NumberInput } from '../../../../NumberInput';
import { EFilterType } from '../../../types/table';

import styles from './InNumberRangeFilter.module.scss';

export interface IInNumberRangeFilterProps {
    header: Header<any, unknown>;
    type: EFilterType;
    inline?: boolean;
}

type TInNumberRangeFilterValue = [number | undefined, number | undefined];

export const InNumberRangeFilter: React.FC<IInNumberRangeFilterProps> = ({ header: { column }, inline = false }) => {
    const { t } = useTranslation();

    const filterValue = useMemo(() => {
        return column.getFilterValue() as TInNumberRangeFilterValue;
    }, [column.getFilterValue()]);

    const [min, setMin] = useState<number | undefined>(filterValue?.[0]);
    const [max, setMax] = useState<number | undefined>(filterValue?.[1]);

    const handleMinChange = (value: number | undefined) => {
        setMin(value);
        setFilterValueDebounced((old: TInNumberRangeFilterValue) => [value, old?.[1]]);
    };
    const handleMaxChange = (value: number | undefined) => {
        setMax(value);
        setFilterValueDebounced((old: TInNumberRangeFilterValue) => [old?.[0], value]);
    };

    const setFilterValueDebounced = useCallback(
        debounce((value) => {
            column.setFilterValue(value);
        }, 300),
        [],
    );

    // Replace local value with the value from the column if it was changed
    useEffect(() => {
        if (!filterValue) {
            setMin(undefined);
            setMax(undefined);
            return;
        }
        setMin(filterValue[0]);
        setMax(filterValue[1]);
    }, [filterValue]);

    return (
        <div className={styles.filterWrapper}>
            <NumberInput
                placeholder={t(inline ? 'table.filtering.minShort' : 'table.filtering.min')}
                size={inline ? 'small' : 'medium'}
                value={min}
                onChange={handleMinChange}
            />
            <NumberInput
                placeholder={t(inline ? 'table.filtering.maxShort' : 'table.filtering.max')}
                size={inline ? 'small' : 'medium'}
                value={max}
                onChange={handleMaxChange}
            />
        </div>
    );
};
